import { PAGE_META, type PageMetaKey } from './pageMeta';
import { PRODUCT_DOWNLOADS } from './productDownloads';
import { FALLBACK_CAMPAIGNS, isCampaignActive } from './campaigns';
import type { SiteSeoConfig } from './siteConfig';
import { SITE_ROUTES } from '../seo/siteRoutes';
import type { SiteLanguage } from '../seo/constants';
import { resolvePageMeta, resolveSearchKeywords } from '../seo/resolvePageMeta';

export type SearchSection = 'cozumler' | 'urunler' | 'hizmetler' | 'diger';

export type SearchEntry = {
  id: string;
  path: string;
  title: string;
  description: string;
  section: SearchSection;
  sectionLabel: string;
  keywords?: string[];
};

const SECTION_LABELS: Record<SearchSection, string> = {
  cozumler: 'Çözümler',
  urunler: 'Ürünler',
  hizmetler: 'Hizmetler',
  diger: 'Genel',
};

function shortTitle(metaTitle: string): string {
  return metaTitle.split('|')[0].trim();
}

type RouteDef = {
  key: PageMetaKey;
  path: string;
  section: SearchSection;
  keywords?: string[];
};

const ROUTE_SECTIONS: Record<string, SearchSection> = {
  home: 'diger',
  cozumler: 'cozumler',
  cozumlerSap: 'cozumler',
  cozumlerLogo: 'cozumler',
  cozumlerDnasoft: 'cozumler',
  cozumlerDnasoftCaf: 'cozumler',
  cozumlerDnasoftKaf: 'cozumler',
  cozumlerBoyum: 'cozumler',
  cozumlerComputec: 'cozumler',
  cozumlerCoresystems: 'cozumler',
  cozumlerLogosoft: 'cozumler',
  cozumlerYapayZeka: 'cozumler',
  hizmetler: 'hizmetler',
  urunler: 'urunler',
  urunlerProintercompany: 'urunler',
  urunlerProbi: 'urunler',
  urunlerProbank: 'urunler',
  urunlerProhr: 'urunler',
  urunlerPromes: 'urunler',
  urunlerPromobile: 'urunler',
  iletisim: 'diger',
  destekIndir: 'diger',
  hakkimizda: 'diger',
  gizlilik: 'diger',
  cerezler: 'diger',
};

export const ROUTE_KEYWORDS: Partial<Record<PageMetaKey, string[]>> = {
  home: ['ana sayfa', 'luminary', 'dijital dönüşüm', 'erp'],
  cozumler: ['çözümler', 'logo', 'sap'],
  cozumlerSap: ['sap b1', 'sap business one', 'erp'],
  cozumlerLogo: ['logo tiger', 'logo go', 'logo start', 'erp'],
  cozumlerDnasoft: ['dnasoft', 'caf', 'kaf', 'mrp'],
  cozumlerDnasoftCaf: ['dnasoft', 'caf', 'konfeksiyon', 'asorti', 'pastal'],
  cozumlerDnasoftKaf: ['dnasoft', 'kaf', 'profil', 'alüminyum', 'plaka'],
  cozumlerBoyum: ['boyum', 'beas', 'produmex', 'perfion', 'eklenti', 'sap b1'],
  cozumlerComputec: ['computec', 'processforce', 'pdc', 'wms', 'mes', 'üretim'],
  cozumlerCoresystems: ['coresystems', 'fsm', 'insightloop', 'chronohub', 'coresuite', 'saha servis'],
  cozumlerLogosoft: ['logosoft', 'microsoft 365', 'azure', 'dynamics', 'acronis', 'huawei', 'bulut', 'güvenlik', 'iot', 'yapay zeka'],
  cozumlerYapayZeka: ['yapay zeka', 'ai', 'erp ai'],
  hizmetler: ['danışmanlık', 'implementasyon', 'destek', 'eğitim'],
  urunler: ['prosoft', 'ürünler'],
  urunlerProintercompany: ['prointercompany', 'şirketler arası', 'konsolidasyon'],
  urunlerProbi: ['probi', 'iş zekası', 'bi', 'dashboard'],
  urunlerProbank: ['probank', 'banka', 'mutabakat', 'mt940'],
  urunlerProhr: ['prohr', 'insan kaynakları', 'bordro', 'ik'],
  urunlerPromes: ['promes', 'mes', 'iot', 'oee', 'üretim'],
  urunlerPromobile: ['promobile', 'wms', 'mobil depo', 'barkod'],
  iletisim: ['iletişim', 'teklif', 'danışmanlık'],
  destekIndir: ['indir', 'demo', 'kurulum', 'probi', 'probank', 'prohr', 'promes', 'caf', 'kaf', 'zip'],
  hakkimizda: ['hakkımızda', 'hikaye', 'kadro'],
  gizlilik: ['gizlilik', 'kvkk', 'kişisel veri'],
  cerezler: ['çerez', 'cookie', 'analitik'],
};

const ROUTES: RouteDef[] = SITE_ROUTES.filter((r) => r.indexable).map((route) => ({
  key: route.pageKey,
  path: route.path,
  section: ROUTE_SECTIONS[route.pageKey] ?? 'diger',
  keywords: ROUTE_KEYWORDS[route.pageKey],
}));

function routeToEntry(route: RouteDef): SearchEntry {
  const meta = PAGE_META[route.key];
  return {
    id: route.key,
    path: route.path,
    title: shortTitle(meta.title),
    description: meta.description,
    section: route.section,
    sectionLabel: SECTION_LABELS[route.section],
    keywords: route.keywords,
  };
}

const downloadKeywords = PRODUCT_DOWNLOADS.map(
  (d) => `${d.name} ${d.description} ${d.buttonLabel} indir`,
);

export const SEARCH_ENTRIES: SearchEntry[] = [
  ...ROUTES.map(routeToEntry),
  {
    id: 'demo-downloads',
    path: '/destek/indir',
    title: shortTitle(PAGE_META.destekIndir.title),
    description: PAGE_META.destekIndir.description,
    section: 'diger',
    sectionLabel: SECTION_LABELS.diger,
    keywords: ['indir', 'demo', 'kurulum', 'zip', ...downloadKeywords],
  },
  ...FALLBACK_CAMPAIGNS.filter(isCampaignActive).map((campaign) => ({
    id: `campaign-${campaign.id}`,
    path: `/kampanya/${campaign.slug}`,
    title: shortTitle(campaign.meta.title),
    description: campaign.meta.description,
    section: 'diger' as SearchSection,
    sectionLabel: 'Kampanya',
    keywords: ['kampanya', campaign.landing.badge, campaign.slug, ...campaign.landing.highlights.map((h) => h.text)],
  })),
];

function enrichEntryKeywords(entry: SearchEntry, seoConfig?: SiteSeoConfig): string[] {
  const base = entry.keywords ?? [];
  if (entry.id.startsWith('campaign-') || entry.id === 'demo-downloads') {
    return base;
  }
  const adminKeywords = resolveSearchKeywords(entry.id as PageMetaKey, seoConfig);
  return [...new Set([...base, ...adminKeywords])];
}

function enrichEntryForSearch(
  entry: SearchEntry,
  seoConfig?: SiteSeoConfig,
  lang: SiteLanguage = 'tr',
): SearchEntry {
  if (entry.id.startsWith('campaign-') || entry.id === 'demo-downloads') {
    return entry;
  }

  const meta = resolvePageMeta(lang, entry.id as PageMetaKey, seoConfig);
  return {
    ...entry,
    title: shortTitle(meta.title),
    description: meta.description,
    keywords: enrichEntryKeywords(entry, seoConfig),
  };
}

export function filterSearchEntries(
  query: string,
  limit = 12,
  seoConfig?: SiteSeoConfig,
  lang: SiteLanguage = 'tr',
): SearchEntry[] {
  const q = query.trim().toLocaleLowerCase('tr');
  if (!q) return [];

  const scored = SEARCH_ENTRIES.map((entry) => {
    const enriched = enrichEntryForSearch(entry, seoConfig, lang);
    const haystack = [
      enriched.title,
      enriched.description,
      enriched.sectionLabel,
      ...(enriched.keywords ?? []),
    ]
      .join(' ')
      .toLocaleLowerCase('tr');

    let score = 0;
    if (enriched.title.toLocaleLowerCase('tr').includes(q)) score += 10;
    if (haystack.includes(q)) score += 5;
    const tokens = q.split(/\s+/).filter(Boolean);
    for (const token of tokens) {
      if (haystack.includes(token)) score += 2;
    }
    return { entry: enriched, score };
  }).filter((item) => item.score > 0);

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((item) => item.entry);
}
